import {
  today,
  toCalendarDateTime,
  parseDate,
  getLocalTimeZone,
  CalendarDate,
} from '@internationalized/date';
import { records } from '@saucelabs/sl-module-user';

import {
  DateFilter,
  DateTimeRequestParams,
  FilterKeys,
  OwnerRequestParams,
  OwnerType,
  RelativeDate,
  RelativeDates,
  ResourceTypes,
  SelectedFilters,
} from './types';

const getToday = (): CalendarDate => today(getLocalTimeZone());

export const mapDateRangeToSearchParams = (dateFilter: DateFilter): DateFilter => {
  const relativeDateStrings = Object.entries(RelativeDates).reduce(
    (acc, [name, dayShift]) => ({
      ...acc,
      [getToday().subtract({ days: dayShift }).toString()]: name,
    }),
    {} as Record<string, RelativeDate>,
  );

  if (
    dateFilter.end === getToday().toString() &&
    dateFilter.start &&
    Object.keys(relativeDateStrings).includes(dateFilter.start)
  ) {
    return {
      start: relativeDateStrings[dateFilter.start],
      end: relativeDateStrings[dateFilter.end],
    };
  }

  return dateFilter;
};

export const isRelativeDateString = (dateString: string): dateString is RelativeDate => (
  Object.keys(RelativeDates).includes(dateString)
);

export const parseRelativeDateString = (dateString: RelativeDate): string => (
  getToday().subtract({ days: RelativeDates[dateString] }).toString()
);

export const getDefaultDateRange = (): DateFilter => ({
  start: getToday().subtract({ days: RelativeDates.last7Days }).toString(),
  end: getToday().toString(),
});

export const getMinDate = (): CalendarDate => getToday().subtract({ days: RelativeDates.allTime });

export const getUnixTimestampInSeconds = (date: Date): number => Math.floor(date.getTime() / 1000);

export const getJSDateFromDateString = (
  dateString: string,
  isEndOfDay = false,
): Date => toCalendarDateTime(
  isEndOfDay ? parseDate(dateString).add({ days: 1 }) : parseDate(dateString),
).toDate(getLocalTimeZone());

export const mapOwnerToQueryParams = (
  selectedOwners: string[],
  initialObject: OwnerRequestParams = {
    org_id: null,
    team_id: null,
    user_id: null,
  },
): OwnerRequestParams => selectedOwners.reduce<OwnerRequestParams>((acc, key) => {
  const [type, id] = key.split('_') as [OwnerType, string];
  const accQueryParam = acc[`${type}_id`];
  if (accQueryParam) {
    if (Array.isArray(accQueryParam) && !accQueryParam.includes(id)) {
      acc[`${type}_id`] = [...accQueryParam, id];
    } else if (!Array.isArray(accQueryParam) && accQueryParam !== id) {
      acc[`${type}_id`] = [accQueryParam, id];
    }
  } else {
    acc[`${type}_id`] = id;
  }
  return acc;
}, initialObject);

export const mapQueryParamsToOwner = (
  searchParams: URLSearchParams,
  defaultOwner?: string,
): string[] => {
  const selectedOwners = [] as string[];
  const userId = searchParams.get('user_id');
  const teamId = searchParams.get('team_id');
  const orgId = searchParams.get('org_id');
  if (userId) {
    selectedOwners.push(`user_${userId}`);
  } else if (teamId) {
    selectedOwners.push(`team_${teamId}`);
  } else if (orgId) {
    selectedOwners.push(`org_${orgId}`);
  } else if (defaultOwner) {
    selectedOwners.push(defaultOwner);
  }

  return selectedOwners;
};

export const mapQueryParamsToDateRange = (
  searchParams: URLSearchParams,
  minDate: CalendarDate = getMinDate(),
  defaultDate: DateFilter = getDefaultDateRange(),
): DateFilter => {
  const start = searchParams.get('start');
  const end = searchParams.get('end');
  if (start && end) {
    if (isRelativeDateString(start) && isRelativeDateString(end)) {
      return {
        start: parseRelativeDateString(start),
        end: parseRelativeDateString(end),
      };
    }
    try {
      const startDate = parseDate(start);
      const endDate = parseDate(end);
      if (startDate.compare(endDate) > 0 || minDate.compare(startDate) > 0) {
        return defaultDate;
      }
      return { start, end };
    } catch {
      return defaultDate;
    }
  }
  return defaultDate;
};

export const mapQueryParamsToFilters = (
  searchParams: URLSearchParams,
): SelectedFilters => Object.values(FilterKeys).reduce<SelectedFilters>(
  (selectedFilters, filterKey) => ({
    ...selectedFilters,
    [filterKey]: searchParams.getAll(filterKey),
  }),
  {},
);

export const getCurrentUserKey = (userId?: string | null): string => `user_${userId ?? ''}`;

export const getCurrentOrgKey = (orgId: string | null): string => `org_${orgId ?? ''}`;

export const mapDateToQueryParams = (
  date: DateFilter,
): DateTimeRequestParams => ({
  start: date.start ?
    getUnixTimestampInSeconds(getJSDateFromDateString(date.start)) :
    undefined,
  end: date.end ?
    getUnixTimestampInSeconds(getJSDateFromDateString(date.end, true)) :
    undefined,
});

const getEmptyValuesForFilterKeys = () => Object.values(FilterKeys).reduce(
  (acc, key) => ({ ...acc, [key]: [] }),
  {},
);

export const getDefaultSelection = (user: records.UserRecord) => ({
  owner: [getCurrentOrgKey(user.organization.id)],
  date: getDefaultDateRange(),
  filters: {
    ...getEmptyValuesForFilterKeys(),
    resourceType: [ResourceTypes.TOTAL_VMS],
  },
});
